import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import usersAdminApi from "../api/usersAdminApi";
import rolesApi from "../api/rolesApi";
import RequirePermission from "../auth/RequirePermission";
import { hasPermission } from "../auth/permissions";
import { tokenStore } from "../api/axiosClient";

/**
 * Admin user list with inline role assignment. Each row has a role dropdown
 * (visible only with users.manage_role). Changing a role calls the backend,
 * which revokes that user's refresh tokens so the change takes effect on
 * their next refresh.
 *
 * Guard rails mirrored from the server:
 *   • You cannot change your OWN role (dropdown disabled on your own row).
 *   • Demoting the last SuperAdmin is rejected (server returns LAST_SUPERADMIN;
 *     we surface a friendly message).
 */
export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const navigate = useNavigate();
  const canManageRole = hasPermission("users.manage_role");

  // Current user's id — to disable the dropdown on their own row.
  const me = tokenStore.getUser();
  const myId = me?.id ?? null;

  const showToast = (msg, tone = "info") => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [usersRes, rolesRes] = await Promise.all([
        usersAdminApi.list(),
        rolesApi.list(),
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapError = (err) => {
    const code = err?.response?.data?.errorCode;
    switch (code) {
      case "CANNOT_CHANGE_OWN_ROLE":
        return "You can't change your own role.";
      case "LAST_SUPERADMIN":
        return "Can't demote the last SuperAdmin. Promote another user first.";
      case "USER_NOT_FOUND":
        return "That user no longer exists.";
      case "ROLE_NOT_FOUND":
        return "That role no longer exists.";
      default:
        return err?.response?.data?.message || "Failed to change role.";
    }
  };

  const handleRoleChange = async (user, newRoleId) => {
    if (newRoleId === user.roleId) return; // no-op

    const newRole = roles.find((r) => r.id === newRoleId);
    const confirmed = window.confirm(
      `Change ${user.email}'s role to "${newRole?.name}"?\n\n` +
        `This will sign them out of all sessions so the new permissions take effect.`
    );
    if (!confirmed) return;

    setSavingId(user.id);
    setError("");
    try {
      const { data } = await usersAdminApi.changeRole(user.id, newRoleId);
      // Update the row in place with the server's response.
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, roleId: data.roleId, roleName: data.roleName } : u
        )
      );
      showToast(`${user.email} is now ${data.roleName}`);
    } catch (err) {
      setError(mapError(err));
      // Re-fetch to reset any optimistic UI drift.
      fetchAll();
    } finally {
      setSavingId(null);
    }
  };

  const fmt = (val) => {
    if (!val) return "Never";
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  return (
    <div className="shell">
      <header className="hero">
        <div>
          <p className="eyebrow">Access Control</p>
          <h1>Users</h1>
          <p className="lede">
            Assign roles to users. Changing a role signs that user out so their
            new permissions apply immediately.
          </p>
        </div>

        <div className="hero-right">
          <div className="actions">
            <button className="ghost" onClick={() => navigate("/")}>
              Dashboard
            </button>
            <button className="ghost" onClick={fetchAll} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <RequirePermission code="roles.read">
              <button className="ghost" onClick={() => navigate("/roles")}>
                Roles
              </button>
            </RequirePermission>
          </div>
        </div>
      </header>

      {toast && <div className={`toast ${toast.tone}`}>{toast.msg}</div>}
      {error && <div className="toast error">{error}</div>}

      <section className="table-wrap">
        <div className="table users">
          <div className="row head">
            <span>Email</span>
            <span>Name</span>
            <span>Status</span>
            <span>Role</span>
            <span>Last Login</span>
          </div>

          {users.map((user) => {
            const isSelf = myId && user.id === myId;
            return (
              <div className="row" key={user.id}>
                <span style={{ fontWeight: 600 }} title={user.email}>
                  {user.email}
                </span>
                <span className="hint">{user.fullName || "—"}</span>
                <span>
                  <span
                    className={`badge ${
                      (user.status || "").toLowerCase() === "active" ? "success" : "neutral"
                    }`}
                  >
                    {user.status || "Unknown"}
                  </span>
                </span>
                <span>
                  {canManageRole ? (
                    <select
                      value={user.roleId || ""}
                      onChange={(e) => handleRoleChange(user, e.target.value)}
                      disabled={isSelf || savingId === user.id}
                      title={isSelf ? "You can't change your own role" : ""}
                      style={{ maxWidth: 200 }}
                    >
                      {roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="badge neutral">{user.roleName || "—"}</span>
                  )}
                </span>
                <span className="hint">{fmt(user.lastLoginAt)}</span>
              </div>
            );
          })}

          {!loading && !users.length && (
            <div className="row empty">No users found</div>
          )}
        </div>
      </section>
    </div>
  );
}